/* eslint-disable react/display-name */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  DatePickerRange,
  Grid,
  LabelText,
  PaginationSize,
  Table,
  tableFilterCss,
  themeUtils as tu,
  useModal,
  usePagination,
  Panel,
} from '@zen-common/components-base';
import { assetUtils, numberUtils, textUtils } from '@zen/common-utils';
import {
  DatePickerRangeStore,
  OracleService,
  SettingsStore,
  TickerAutoSuggest,
  TitleContentLayout,
} from '@zen/common-app-parts';
import styled, { css } from 'styled-components';
import { usePaginatedQuery } from 'react-query';
import { useLoadingOnUserGesture } from '@zen/common-react-hooks';
import { useFetchCommitmentsCount } from '../../stores/OracleQueries';
import useContract from '../../hooks/useContract';
import TopParagraph from '../../components/TopParagraph/TopParagraph';
import ShowMoreResult from '../../components/form/ShowMoreResult';
import OraclesDropDownFG from '../../components/form/edit/OraclesDropDownFG/OraclesDropDownFG';
import _oracles from '../../utils/oracles';

export default function Commitments() {
  const isMobile = document.body.clientWidth < tu.breakpoints.xl.replace('px', '');
  const oracles = _oracles.map((o) => ({
    label: isMobile
      ? `${o.chain} ${assetUtils.truncateOrNameAsset(o.address)}`
      : `${o.chain} ${o.address}`,
    value: o.address,
  }));

  const { address } = useContract();
  const [data, setData] = useState([]);
  const store = DatePickerRangeStore.useStore();
  const {
    state: { startDate, endDate },
  } = store;
  const [ticker, onTickerChanged] = useState(undefined);
  const start = useMemo(() => startDate && startDate.setHours(0, 0, 0), [startDate]);
  const expiry = useMemo(() => endDate && endDate.setHours(23, 59, 59, 999), [endDate]);
  const {
    state: {
      settings: { oracleUrl, chain },
    },
    actions,
  } = SettingsStore.useStore();

  const { data: queryCount, refetch: refetchCount } = useFetchCommitmentsCount(5000, {
    ticker,
    start,
    expiry,
  });

  const [oracle, setOracleState] = React.useState(_oracles[1]);

  const pagination = usePagination({
    manual: true,
    itemsCount: queryCount || 0,
  });

  const setOracle = (item) => {
    const o = _oracles.find((o) => o.address === item.value);
    setOracleState(o || null);
    actions.setSetting({ name: 'oracleUrl', value: o.serviceUrl });
    actions.setSetting({ name: 'chain', value: o.chain });
    pagination.onPageChange(0);
  };

  const { resolvedData, latestData, isFetching } = usePaginatedQuery(
    [
      'commitments-paginated',
      {
        skip: pagination.page * pagination.pageSize,
        take: pagination.pageSize,
        ticker,
        start,
        expiry,
      },
      queryCount,
      oracleUrl,
    ],
    (_, params) => {
      // if the commitments count is higher then the current skip we set page to 0
      if (queryCount < pagination.page * pagination.pageSize) pagination.onPageChange(0);
      refetchCount({ force: true });
      return OracleService.query(oracleUrl, params);
    },
    { enabled: queryCount }
  );

  React.useEffect(() => {
    if (!resolvedData) return;
    return setData(resolvedData);
  }, [resolvedData]);

  React.useEffect(() => {
    setOracleState(_oracles.find((o) => o.chain === chain) || null);
  }, [actions, chain]);

  const loading = useLoadingOnUserGesture({
    isFetching,
    latestData,
    page: pagination.page,
    pageSize: pagination.pageSize,
  });

  const tableData = useMemo(() => (queryCount === 0 ? [] : data), [queryCount, data]);

  return (
    <TitleContentLayout
      dark
      noPadding
      title="ZP Oracle"
      renderBelowTitle={<TopParagraph address={address} />}
    >
      <MainContainer>
        <ColoredBod>
          <StyledBox width={isMobile ? '100%' : '70%'}>
            <OraclesDropDownFG items={oracles} value={oracle} onChange={setOracle} />
          </StyledBox>
          <FiltersContainer>
            <Grid.Row>
              <ColDate>
                <LabelText>Search date</LabelText>
                <StyledModalContainer>
                  <DatePickerRange store={store} id="date-filter" />
                </StyledModalContainer>
              </ColDate>
              <ColTicker>
                <TickerAutoSuggest showTitle ticker={ticker} onTickerChanged={onTickerChanged} />
              </ColTicker>
            </Grid.Row>
          </FiltersContainer>
          <PageSizeContainer>
            <PaginationSize {...pagination} css={tableFilterCss} width={['100%', 'auto']} />
          </PageSizeContainer>
        </ColoredBod>
        <Divider />
        <Table
          data={tableData}
          loading={loading}
          columns={tableColumns}
          resizable={false}
          sortable={false}
          minRows={0}
          {...pagination}
        />
      </MainContainer>
    </TitleContentLayout>
  );
}

const MainContainer = styled(Box)`
  position: relative;
`;

const StyledModalContainer = styled(Box)`
  position: relative;
  z-index: 10000 !important;
`;

const PageSizeContainer = styled(Panel)`
  top: 175px;
  display: flex;
  border-color: ${tu.color('borderDark')};
  border-style: solid;
  border-width: 0;
  ${css(
    tu.mq({
      position: ['static', 'absolute'],
      right: ['0', tu.space('sm')],
      padding: [tu.space('smd'), '0'],
      borderBottomWidth: ['1px', 0],
    })
  )}
`;

const FiltersContainer = styled(Panel)`
  border-color: ${tu.color('borderDark')};
  border-style: solid;
  border-width: 0;
  ${css(
    tu.mq({
      padding: [tu.space('smd')],
    })
  )}
`;

const ColDate = styled(Box)`
  ${tu.mq({
    marginLeft: [0, null, tu.space('xs')],
    marginRight: [0, null, tu.space('xs')],
    marginBottom: [tu.space('xs'), null, 0],
  })}
  &:first-child {
    margin-left: 0;
  }
  &:last-child {
    margin-right: 0;
    margin-bottom: 0;
  }
`;

const ColTicker = styled(Box)`
  ${tu.mq({
    width: ['100%', null, '10%'],
  })}

  ${tu.mq({
    marginLeft: [0, null, tu.space('xs')],
    marginRight: [0, null, tu.space('xs')],
    marginBottom: [tu.space('xs'), null, 0],
  })}
  &:first-child {
    margin-left: 0;
  }
  &:last-child {
    margin-right: 0;
    margin-bottom: 0;
  }
`;

const Divider = styled(Box)`
  height: 0;
  flex-grow: 1;
  flex-shrink: 0;
  border-bottom: 1px solid ${tu.color('borderDark')};
`;

const ColoredBod = styled(Box)`
  margin-top: -5px;
  background-color: ${tu.color('bg2')};
`;
const StyledBox = styled(Box)`
  justify-content: center;
  margin-top: 5px;
  ${css(
    tu.mq({
      paddingTop: [tu.space('smd')],
      paddingLeft: [tu.space('smd')],
      paddingRight: [tu.space('smd')],
    })
  )}
`;

const ShowMore = ({ values, ...props }) => {
  const { show, hide } = useModal();

  const openModal = () => {
    show(() => <ShowMoreResult values={values} hide={hide} />);
  };

  return (
    <ActionButton bg="secondary" onClick={openModal} {...props}>
      More
    </ActionButton>
  );
};

const tableColumns = [
  {
    Header: 'Timestamp',
    accessor: 'timestamp',
    minWidth: 200,
    Cell: ({ value }) => textUtils.getDateStringFromTimestamp(value),
  },
  {
    Header: 'Asset Name',
    accessor: 'item.key',
    minWidth: 150,
    Cell: ({ value }) => value,
  },
  {
    Header: 'Price',
    accessor: 'item.value',
    minWidth: 150,
    Cell: ({ value }) => '$ ' + numberUtils.getPrice(value),
  },
  {
    Header: 'Show more',
    accessor: '',
    minWidth: 180,
    className: 'align-right',
    headerClassName: 'align-right',
    Cell: ({ original }) => (
      <Box>
        <ShowMore values={original} />
      </Box>
    ),
  },
];

const ActionButton = styled(Button)`
  padding: ${tu.space('xxs')};
  width: 100px;
`;
